import { useAuthStore } from "@/store/auth.store";
import { useProfileStore } from "@/store/profile.store";
import {
  Profile,
  ProfileCredits,
  UpdateProfileResponse,
  UpdateProfilePayload,
  PhoneVerificationCodeResponseError,
  RequestPhoneVerificationCodeRequestResponseData,
  SubmitPhoneVerificationCodeRequestPayload,
  SubmitPhoneVerificationCodeRequestData,
} from "@/types/profile.types";
import { getCookie } from "@/utils/cookies.utils";

/**
 * Fetches the user profile from the API and includes nux data in the response.
 * @param accessToken - The access token for the user.
 * @returns The user profile.
 */
const fetchUserProfile = async (accessToken?: string): Promise<Profile> => {
  const token = accessToken || getCookie("PSAccess");
  if (!token) {
    // clean up profile and auth store on missing token as this likely means the token was deleted/expired and we want to make sure to clear any stale data and reset to a logged out state
    useProfileStore.getState().clearProfile();
    useAuthStore.getState().logout();
    throw new Error("Access token is required");
  }

  try {
    // The expTrigger is used to include nux data in the response (uncompletedExps field)
    const response = await fetch("/v1/profiles/me?expTrigger=page-load", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch user profile with status: ${response.status}`,
      );
    }

    const profileData: Profile = await response.json();
    // Update profile store
    useProfileStore.getState().setProfile(profileData);
    // Return profile data
    return profileData;
  } catch (error) {
    // Update profile store
    useProfileStore.getState().clearProfile();
    // we also want to clear the access token on failure to fetch profile as this likely means the token is invalid and will continue to cause errors until cleared
    useAuthStore.getState().logout();
    throw new Error("Failed to fetch user profile", { cause: error });
  }
};

const updateProfile = async ({
  accessToken,
  payload,
  path = "me",
}: {
  accessToken?: string;
  payload: Partial<Profile> | UpdateProfilePayload;
  path?: string;
}): Promise<UpdateProfileResponse> => {
  const token = accessToken || getCookie("PSAccess");
  if (!token) {
    throw new Error("Access token is required");
  }

  const isUserOnFA = useProfileStore.getState().profile?.ext_user_id;

  const shouldUseNewEndpoint = isUserOnFA;

  const url = shouldUseNewEndpoint
    ? `/v2/profiles/update-details`
    : `/v1/profiles/${path}`;

  const method = shouldUseNewEndpoint ? "POST" : "PUT";

  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to update profile with status: ${response.status}`,
      );
    }

    const data = await response.json();
    useProfileStore.getState().setProfile(data.profile);

    return data;
  } catch (error) {
    throw new Error("Failed to update profile", { cause: error });
  }
};

const fetchProfileCredits = async (
  accessToken: string,
  ssoId: string,
): Promise<ProfileCredits> => {
  if (!accessToken) {
    throw new Error("Access token is required");
  }
  try {
    const response = await fetch(`/v1/profiles/${ssoId}/credit`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch profile credits with status: ${response.status}`,
      );
    }

    const data = (await response.json()) as ProfileCredits;
    return data;
  } catch (error) {
    throw new Error("Failed to fetch profile credits", { cause: error });
  }
};

const fetchProfileExperiences = async () => {
  const accessToken = getCookie("PSAccess");
  if (!accessToken) {
    throw new Error("Access token is required");
  }
  const response = await fetch(`/v1/profiles/me/experience?name=terms`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch profile experiences with status: ${response.status}`,
    );
  }

  const data = await response.json();
  return data;
};

const requestPhoneVerificationCode = async () => {
  const accessToken = getCookie("PSAccess");
  if (!accessToken) {
    throw new Error("Access token is required");
  }

  const response = await fetch("/v2/profiles/phone-verification/start", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const data:
    | RequestPhoneVerificationCodeRequestResponseData
    | PhoneVerificationCodeResponseError = await response.json();

  if (!response.ok || "error" in data || "message" in data) {
    throw data;
  }

  return data as RequestPhoneVerificationCodeRequestResponseData;
};

const submitPhoneVerificationCode = async (
  payload: SubmitPhoneVerificationCodeRequestPayload,
) => {
  const accessToken = getCookie("PSAccess");
  if (!accessToken) {
    throw new Error("Access token is required");
  }

  const response = await fetch("/v2/profiles/phone-verification/complete", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  const data: SubmitPhoneVerificationCodeRequestData = await response.json();

  if (!response.ok || "error" in data || "message" in data) {
    throw data;
  }

  // at this point the user is phone verified - update state
  useProfileStore.getState().setProfile({ phone_verified: true });

  return data;
};

const sendEmailVerificationCode = async () => {
  const accessToken = getCookie("PSAccess");
  if (!accessToken) {
    throw new Error("Access token is required");
  }

  const isUserOnFA = useProfileStore.getState().profile?.ext_user_id;

  const shouldUseNewEndpoint = isUserOnFA;

  const url = shouldUseNewEndpoint
    ? "/v2/profiles/email-verification/start"
    : "/v1/profiles/me/email/verify";

  const method = shouldUseNewEndpoint ? "POST" : "GET";

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  const data = await response.json();

  // errors will come back as messages
  if (data.error || (shouldUseNewEndpoint && "message" in data)) {
    throw data;
  }

  return shouldUseNewEndpoint
    ? (data as {
        verification_id: string;
        code?: string;
      })
    : (data as { message: string });
};

const verifyUserEmail = async ({
  code,
  verificationId,
}: {
  code: string;
  verificationId?: string;
}) => {
  const accessToken = getCookie("PSAccess");
  if (!accessToken) {
    throw new Error("Access token is required");
  }

  const isUserOnFA = useProfileStore.getState().profile?.ext_user_id;

  const shouldUseNewEndpoint = isUserOnFA;

  if (shouldUseNewEndpoint && !verificationId) {
    throw new Error("No verification id for verifying user email");
  }

  const url = shouldUseNewEndpoint
    ? "/v2/profiles/email-verification/complete"
    : "/v1/profiles/me/email/verify";

  const method = shouldUseNewEndpoint ? "POST" : "PUT";

  const body = JSON.stringify(
    shouldUseNewEndpoint
      ? { code, verification_id: verificationId }
      : { email_verification_code: code },
  );

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body,
  });

  const data = await response.json();

  if (
    !response.ok ||
    "error" in data ||
    (shouldUseNewEndpoint && "message" in data)
  ) {
    throw new Error(
      `Failed to verify user email with status: ${response.status}`,
    );
  }

  // at this point the user is email verified - update state
  useProfileStore.getState().setProfile({ email_verified: true });

  return data;
};
export {
  fetchUserProfile,
  fetchProfileCredits,
  fetchProfileExperiences,
  requestPhoneVerificationCode,
  updateProfile,
  sendEmailVerificationCode,
  submitPhoneVerificationCode,
  verifyUserEmail,
};
